import React, { useState, useEffect } from 'react';
import tw from 'twin.macro';
import styled from 'styled-components';
import { fetchCategories, fetchMarkets } from 'DBServices/firestoreDBService';

const Overlay = styled.div`${tw`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50`}`;
const Modal = styled.div`${tw`bg-white rounded-lg p-8 w-full max-w-lg max-h-screen overflow-y-auto`}`;
const Title = tw.h2`text-xl font-bold mb-6 text-gray-800`;
const Label = tw.label`block text-sm font-semibold text-gray-600 mb-1`;
const Input = tw.input`w-full border border-gray-300 rounded px-3 py-2 mb-4 text-sm focus:outline-none focus:border-green-500`;
const Row = tw.div`flex gap-3`;
const HalfInput = tw.input`w-full border border-gray-300 rounded px-3 py-2 mb-4 text-sm focus:outline-none focus:border-green-500`;
const ButtonRow = tw.div`flex justify-end gap-3 mt-2`;
const SaveBtn = tw.button`bg-green-500 text-white px-5 py-2 rounded font-semibold hover:bg-green-600 transition`;
const CancelBtn = tw.button`bg-gray-200 text-gray-700 px-5 py-2 rounded font-semibold hover:bg-gray-300 transition`;

const EMPTY = { title: '', content: '', contentArray: '', queryBy: '', price: '', rating: 0, reviews: 0, imageSrc: '', url: '#', type: [], categories: [], partnerMarketId: '', marketId: '' };

const NEEDS_CONTENT_ARRAY = ['Market', 'Category'];

export default function AdminProductForm({ item, onSave, onCancel, collectionLabel, partners = [], isPartner = false }) {
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [expandedType, setExpandedType] = useState(-1);
    const [editingMeasure, setEditingMeasure] = useState({ typeIndex: -1, measureIndex: -1 });
    const [availableCategories, setAvailableCategories] = useState([]);
    const [availableMarkets, setAvailableMarkets] = useState([]);

    useEffect(() => {
        fetchCategories().then(cats => setAvailableCategories(cats || [])).catch(() => {});
        fetchMarkets().then(mkts => setAvailableMarkets(mkts || [])).catch(() => {});
    }, []);

    useEffect(() => {
        setForm(item ? {
            title: item.title || '',
            content: item.content || '',
            contentArray: Array.isArray(item.contentArray) ? item.contentArray.join(', ') : (item.contentArray || ''),
            queryBy: item.queryBy || '',
            price: item.price || '',
            rating: item.rating !== undefined ? item.rating : 0,
            reviews: item.reviews !== undefined ? item.reviews : 0,
            imageSrc: item.imageSrc || '',
            url: item.url || '#',
            categories: Array.isArray(item.categories) ? item.categories : [],
            partnerMarketId: item.partnerMarketId || '',
            marketId: item.marketId || '',
            type: Array.isArray(item.type) ? item.type.map(t => ({
                ...t,
                Measure_and_price: Array.isArray(t.Measure_and_price) ? t.Measure_and_price.map(m => ({ ...m })) : []
            })) : []
        } : EMPTY);
    }, [item]);

    const set = (field) => (e) => { const val = e.target.value; setForm(f => ({ ...f, [field]: val })); };

    const handleSubmit = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!form.title.trim()) { alert('Title is required'); return; }
        if (!form.imageSrc.trim()) { alert('Image URL is required'); return; }
        if (collectionLabel === 'Category' && !form.queryBy.trim()) { alert('Query Key is required for Categories'); return; }
        // sanitize and validate type/measure structure before sending
        const sanitizedType = (form.type || []).map(t => ({
            title: t.title || '',
            imageSrc: t.imageSrc || '',
            Measure_and_price: (t.Measure_and_price || []).map(m => ({
                measure: (m && m.measure) ? m.measure : '',
                price: (m && m.price) ? m.price : '',
                imageSrc: (m && m.imageSrc) ? m.imageSrc : ''
            })).filter(mm => mm.measure || mm.price)
        }));

        // If this is a Foods item, require at least one category
        if (collectionLabel === 'Foods') {
            if (sanitizedType.length === 0) { alert('At least one type is required for Foods. Add a type with measures.'); return; }
            const hasMeasure = sanitizedType.some(t => (t.Measure_and_price || []).length > 0);
            if (!hasMeasure) { alert('Each type must have at least one measure/price.'); return; }
            if (!form.categories || form.categories.length === 0) { alert('Please assign at least one category to this food item.'); return; }
            if (!form.marketId) { alert('Please select a market location for this food item.'); return; }
        }

        const data = {
            ...form,
            type: sanitizedType,
            price: form.price.toString(),
            rating: isPartner ? '0' : form.rating.toString(),
            reviews: isPartner ? '0' : form.reviews.toString(),
            categories: form.categories || [],
            partnerMarketId: form.partnerMarketId || '',
            marketId: form.marketId || '',
        };
        if (NEEDS_CONTENT_ARRAY.includes(collectionLabel)) {
            data.contentArray = form.contentArray
                ? form.contentArray.split(',').map(s => s.trim()).filter(Boolean)
                : [];
        }
        setSaving(true);
        try {
            await onSave(data);
        } catch (err) {
            console.error('AdminProductForm save error:', err);
            alert('Save failed: ' + (err?.message || JSON.stringify(err)));
        } finally {
            setSaving(false);
        }
    };

    // Type / Measure helpers
    const addType = () => {
        setForm(f => ({ ...f, type: [...(f.type || []), { title: '', imageSrc: '', Measure_and_price: [] }] }));
        setExpandedType((form.type || []).length);
    };
    const updateTypeField = (index, field, value) => {
        setForm(f => ({ ...f, type: f.type.map((t, i) => i === index ? { ...t, [field]: value } : t) }));
    };
    const removeType = (index) => {
        setForm(f => ({ ...f, type: f.type.filter((_, i) => i !== index) }));
        setExpandedType(-1);
    };
    const addMeasure = (typeIndex) => {
        setForm(f => ({ ...f, type: f.type.map((t, i) => i === typeIndex ? { ...t, Measure_and_price: [...(t.Measure_and_price||[]), { measure: '', price: '', imageSrc: '' }] } : t) }));
        setEditingMeasure({ typeIndex, measureIndex: (form.type?.[typeIndex]?.Measure_and_price || []).length });
    };
    const updateMeasureField = (typeIndex, measureIndex, field, value) => {
        setForm(f => ({ ...f, type: f.type.map((t, i) => i === typeIndex ? { ...t, Measure_and_price: t.Measure_and_price.map((m, mi) => mi === measureIndex ? { ...m, [field]: value } : m) } : t) }));
    };
    const removeMeasure = (typeIndex, measureIndex) => {
        setForm(f => ({ ...f, type: f.type.map((t, i) => i === typeIndex ? { ...t, Measure_and_price: t.Measure_and_price.filter((_, mi) => mi !== measureIndex) } : t) }));
        setEditingMeasure({ typeIndex: -1, measureIndex: -1 });
    };

    return (
        <Overlay onClick={onCancel}>
            <Modal onClick={e => e.stopPropagation()}>
                <Title>{item?.firestoreId ? 'Edit' : 'Add'} {collectionLabel} Item</Title>
                <form onSubmit={handleSubmit}>
                    <Label>Title *</Label>
                    <Input value={form.title} onChange={set('title')} placeholder="e.g. Rice" required />

                    {collectionLabel !== 'Category' && (
                        <>
                            <Label>Description</Label>
                            <Input value={form.content} onChange={set('content')} placeholder="e.g. Flakes, Grains, Cereals and Legumes" />
                        </>
                    )}

                    {NEEDS_CONTENT_ARRAY.includes(collectionLabel) && (
                        <>
                            {collectionLabel === 'Market' && (
                                <>
                                    <Label>Items (comma-separated)</Label>
                                    <Input
                                        value={form.contentArray}
                                        onChange={set('contentArray')}
                                        placeholder="e.g. Rice, Beans, Garri"
                                    />
                                </>
                            )}
                            <Label>Query Key (no spaces, use underscores)</Label>
                            <Input value={form.queryBy} onChange={set('queryBy')} placeholder="e.g. Tubers or Mama_Bola_Tuber_Stores" />
                        </>
                    )}

                    {(collectionLabel === 'Foods' || collectionLabel === 'Featured') && (
                        <>
                            <Row>
                                <div style={{ flex: 1 }}>
                                    <Label>Price (₦)</Label>
                                    <HalfInput value={form.price} onChange={set('price')} placeholder="e.g. 400" type="number" min="0" />
                                </div>
                                {!isPartner && (
                                    <div style={{ flex: 1 }}>
                                        <Label>Rating (0–5)</Label>
                                        <HalfInput value={form.rating} onChange={set('rating')} placeholder="e.g. 4.8" type="number" min="0" max="5" step="0.1" />
                                    </div>
                                )}
                                {!isPartner && (
                                    <div style={{ flex: 1 }}>
                                        <Label>Reviews</Label>
                                        <HalfInput value={form.reviews} onChange={set('reviews')} placeholder="e.g. 87" type="number" min="0" />
                                    </div>
                                )}
                            </Row>

                            {!isPartner && (
                                <>
                                    <Label>Product URL</Label>
                                    <Input value={form.url} onChange={set('url')} placeholder="#" />
                                </>
                            )}
                        </>
                    )}

                    <Label>Image URL</Label>
                    <Input value={form.imageSrc} onChange={set('imageSrc')} placeholder="https://..." />

                    {/* Category assignment for Foods */}
                    {collectionLabel === 'Foods' && (
                        <>
                            <Label>Categories * <span style={{ color: '#ef4444' }}>(required)</span></Label>
                            {availableCategories.length === 0 ? (
                                <p style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: 8 }}>No categories found. Create categories first.</p>
                            ) : (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: 12 }}>
                                    {availableCategories.map(cat => (
                                        <button
                                            key={cat.firestoreId}
                                            type="button"
                                            onClick={() => {
                                                const id = cat.firestoreId;
                                                setForm(f => ({
                                                    ...f,
                                                    categories: f.categories.includes(id)
                                                        ? f.categories.filter(c => c !== id)
                                                        : [...f.categories, id]
                                                }));
                                            }}
                                            style={{
                                                padding: '3px 10px', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer',
                                                background: form.categories.includes(cat.firestoreId) ? '#10b981' : '#f3f4f6',
                                                color: form.categories.includes(cat.firestoreId) ? 'white' : '#374151',
                                                border: form.categories.includes(cat.firestoreId) ? '1px solid #10b981' : '1px solid #d1d5db'
                                            }}
                                        >
                                            {cat.title}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {!isPartner && (
                                <>
                                    <Label>Market <span style={{ color: '#ef4444' }}>*</span> <span style={{ color: '#9ca3af', fontWeight: 'normal', fontSize: '0.78rem' }}>(required for Foods)</span></Label>
                                    <select
                                        value={form.marketId}
                                        onChange={e => setForm(f => ({ ...f, marketId: e.target.value }))}
                                        style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 6, padding: '8px 12px', fontSize: '0.85rem', marginBottom: 12 }}
                                    >
                                        <option value="">-- No market assigned --</option>
                                        {availableMarkets.map(m => (
                                            <option key={m.firestoreId} value={m.firestoreId}>{m.title}</option>
                                        ))}
                                    </select>
                                </>
                            )}
                        </>
                    )}

                    {/* Partner market assignment for Market items */}
                    {collectionLabel === 'Market' && partners.length > 0 && (
                        <>
                            <Label>Partner Market (Optional)</Label>
                            <select
                                value={form.partnerMarketId}
                                onChange={e => setForm(f => ({ ...f, partnerMarketId: e.target.value }))}
                                style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 6, padding: '8px 12px', fontSize: '0.85rem', marginBottom: 12 }}
                            >
                                <option value="">-- No partner market --</option>
                                {partners.filter(p => p.partnerType === 'market' && p.verificationStatus === 'approved').map(p => (
                                    <option key={p.firestoreId} value={p.firestoreId}>{p.businessName} ({p.name})</option>
                                ))}
                            </select>
                        </>
                    )}

                    {(collectionLabel === 'Foods' || collectionLabel === 'Featured') && (
                        <>
                            <Label>Types</Label>
                            <div>
                                {(form.type || []).map((t, ti) => (
                                    <div key={ti} style={{ border: '1px solid #e5e7eb', padding: 10, borderRadius: 6, marginBottom: 8 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div style={{ fontWeight: 600 }}>{t.title || `Type ${ti+1}`}</div>
                                            <div>
                                                <button type="button" onClick={() => setExpandedType(expandedType === ti ? -1 : ti)} style={{ marginRight: 8 }}>
                                                    {expandedType === ti ? 'Collapse' : 'Edit'}
                                                </button>
                                                <button type="button" onClick={() => removeType(ti)} style={{ color: 'red' }}>Remove</button>
                                            </div>
                                        </div>
                                        {expandedType === ti && (
                                            <div style={{ marginTop: 8 }}>
                                                <Label>Type Title</Label>
                                                <Input value={t.title} onChange={e => updateTypeField(ti, 'title', e.target.value)} placeholder="e.g. foreign_rice" />
                                                <Label>Type Image URL</Label>
                                                <Input value={t.imageSrc} onChange={e => updateTypeField(ti, 'imageSrc', e.target.value)} placeholder="https://..." />
                                                <Label>Measures</Label>
                                                <div>
                                                    {(t.Measure_and_price || []).map((m, mi) => (
                                                        <div key={mi} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                                            <div style={{ flex: 1 }}>
                                                                <div style={{ fontWeight: 600 }}>{m.measure || `Measure ${mi+1}`}</div>
                                                                <div style={{ color: '#6b7280' }}>₦{m.price || ''}</div>
                                                            </div>
                                                            <div>
                                                                <button type="button" onClick={() => setEditingMeasure({ typeIndex: ti, measureIndex: mi })} style={{ marginRight: 6 }}>Edit</button>
                                                                <button type="button" onClick={() => removeMeasure(ti, mi)} style={{ color: 'red' }}>Remove</button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                    {editingMeasure.typeIndex === ti && editingMeasure.measureIndex > -1 && (
                                                        <div style={{ padding: 8, border: '1px dashed #d1d5db', borderRadius: 6, marginBottom: 6 }}>
                                                            <Label>Measure</Label>
                                                            <Input value={(t.Measure_and_price[editingMeasure.measureIndex]?.measure) || ''} onChange={e => updateMeasureField(ti, editingMeasure.measureIndex, 'measure', e.target.value)} />
                                                            <Label>Price</Label>
                                                            <Input value={(t.Measure_and_price[editingMeasure.measureIndex]?.price) || ''} onChange={e => updateMeasureField(ti, editingMeasure.measureIndex, 'price', e.target.value)} type="number" />
                                                            <Label>Image URL</Label>
                                                            <Input value={(t.Measure_and_price[editingMeasure.measureIndex]?.imageSrc) || ''} onChange={e => updateMeasureField(ti, editingMeasure.measureIndex, 'imageSrc', e.target.value)} />
                                                            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                                                <CancelBtn type="button" onClick={() => setEditingMeasure({ typeIndex: -1, measureIndex: -1 })}>Done</CancelBtn>
                                                            </div>
                                                        </div>
                                                    )}
                                                    <button type="button" onClick={() => addMeasure(ti)} style={{ fontSize: '0.8rem', color: '#3b82f6', fontWeight: 600 }}>+ Add Measure</button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                            <button type="button" onClick={addType} style={{ color: '#10b981', fontWeight: 600, fontSize: '0.85rem' }}>+ Add Type</button>
                        </>
                    )}

                    <ButtonRow>
                        <CancelBtn type="button" onClick={onCancel} disabled={saving}>Cancel</CancelBtn>
                        <SaveBtn type="button" onClick={handleSubmit} disabled={saving}>
                            {saving ? 'Saving...' : 'Save'}
                        </SaveBtn>
                    </ButtonRow>
                </form>
            </Modal>
        </Overlay>
    );
}
